<template>
  <div class="lg:container lg:mx-auto overflow-hidden">
    <Head title="Dashboard" />

    <Transition name="slide-fade">
      <div v-show="showFilters" class="fixed top-0 right-0 bottom-0 w-1/2 md:w-1/3 lg:w-1/4 z-40 p-8 bg-white">
        <button aria-label="close" class="absolute top-0 right-0 text-4xl text-gray-500 my-2 mx-4" @click.prevent="showFilters = false">×</button>

        <div class="pb-2 w-full">
          <label class="form-label">Team Member</label>
          <v-multiselect
            v-model="query.members"
            :options="members"
            placeholder="All Team Members"
            label="label"
            track-by="value"
            :multiple="true"
            :close-on-select="false"
          />
        </div>

        <div class="pb-2 w-full">
          <label class="form-label">Project Title</label>
          <v-multiselect
            v-model="query.titles"
            :options="titles"
            placeholder="All Projects"
            label="label"
            track-by="value"
            :multiple="true"
            :close-on-select="false"
          />
        </div>

        <div class="pb-2 w-full">
          <label class="form-label">Company</label>
          <v-multiselect
            v-model="query.companies"
            :options="companies"
            placeholder="All Companies"
            label="label"
            track-by="value"
            :multiple="true"
            :close-on-select="false"
          />
        </div>

        <div class="pb-2 w-full">
          <label class="form-label">Project Owner</label>
          <v-multiselect
            v-model="query.owners"
            :options="owners"
            placeholder="All Project Owners"
            label="label"
            track-by="value"
            :multiple="true"
            :close-on-select="false"
          />
        </div>

        <div class="pb-2 w-full">
          <label class="form-label">Quote Status</label>
          <v-multiselect
            v-model="query.statuses"
            :options="statuses"
            placeholder="All"
            label="label"
            track-by="value"
            :multiple="true"
            :close-on-select="false"
          />
        </div>

        <div class="pb-2 w-full">
          <label class="form-label">Sales Region</label>
          <v-multiselect
            v-model="query.regions"
            :options="regions"
            placeholder="All"
            label="label"
            track-by="value"
            :multiple="true"
            :close-on-select="false"
          />
        </div>

        <div class="mt-4 mb-6 flex items-center justify-between">
          <p class="font-bold">Exclude Duplicated Projects</p>
          <checkbox-input v-model="query.duplicated" />
        </div>

        <div class="mt-4 mb-20 flex items-center justify-between">
          <p class="font-bold">Include Closed Quotes</p>
          <checkbox-input v-model="query.closed" />
        </div>

        <div class="flex items-stretch gap-4 justify-end">
          <button type="button" class="border border-blue-400 py-2 px-8" @click="reset">Clear</button>
          <button type="button" class="btn-blue px-8" @click="filter">Filter</button>
        </div>
      </div>
    </Transition>
    <!-- sidebar -->

    <div class="flex items-center border-b-2 border-gray-300 pb-3 mb-8">
      <div class="flex-auto">
        <h1 class="text-4xl font-bold w-1/3 md:w-1/6">Dashboard</h1>
      </div>
      <div class="flex-auto flex items-strech gap-8 justify-end">
        <div class="flex items-center border border-blue-400 px-2">
          <date-range-picker ref="picker" v-model="dateRange" :ranges="ranges" :auto-apply="true" @update="update">
            <template #input="picker">
              <span>{{ getDate(picker.startDate) }} - {{ getDate(picker.endDate) }}</span>
            </template>
            <!--    footer slot-->
            <template #footer="data">
              <div class="flex items-center gap-4 justify-end my-2 py-1 px-2 text-sm">
                <div>
                  {{ data.rangeText }}
                </div>
                <div class="flex items-stretch justify-around gap-2">
                  <button type="button" class="border border-blue-400 py-1 px-2" @click="data.clickCancel">Cancel</button>
                  <button v-if="! data.in_selection" type="button" class="btn-blue py-1 px-2" @click="data.clickApply">Apply</button>
                </div>
              </div>
            </template>
          </date-range-picker>
          <icon name="calendar" class="w-4 h-4" />
        </div>
        <button type="button" class="border border-blue-400 py-2 px-8" @click="today">Today</button>
        <button type="button" class="btn-blue px-8 " @click="showFilters = ! showFilters">Filters</button>
      </div>
    </div>
    <!-- Title -->


    <div class="flex items-center justify-around mb-8 text-center">
      <div class="px-12 py-6 bg-blue-800 text-white">
        <p class="font-bold mb-4 h-8">Projects</p>
        <p class="font-bold text-5xl">{{ metrics.count }}</p>
      </div>
      <div class="px-12 py-6 bg-blue-800 text-white">
        <p class="font-bold mb-4 h-8">Quotes <br /> Pending</p>
        <p class="font-bold text-5xl">{{ metrics.pending }}</p>
      </div>
      <div class="px-12 py-6 bg-blue-800 text-white">
        <p class="font-bold mb-4 h-8">Quotes <br /> Won</p>
        <p class="font-bold text-5xl">{{ metrics.won }}</p>
      </div>
      <div class="px-12 py-6 bg-blue-800 text-white">
        <p class="font-bold mb-4 h-8">Closing <br /> Rate</p>
        <p class="font-bold text-5xl">{{ metrics.rate }}%</p>
      </div>
    </div>
    <!-- metrics -->

    <div class="flex items-center justify-between border-b-2 border-gray-300 pb-4 mb-8">
      <div class="flex items-center w-1/5">
        <label for="group_by" class="form-label w-1/2 m-0 text-xl">Group by:</label>
        <select id="group_by" v-model="form.group_by" name="group_by" class="form-select" @change="search">
          <option value="date">Date</option>
          <option value="company">Company</option>
          <option value="owner">Project Owner</option>
          <option value="title">Project Name</option>
          <option value="status">Project Status</option>
          <option value="region">Project Region</option>
        </select>
      </div>
      <div class="flex items-center gap-8 justify-end">
        <button type="button" @click="toggleGroups">
          {{ ! expandGroups ? 'Collapse' : 'Expand' }}
          <icon :name="! expandGroups ? 'cheveron-up' : 'cheveron-down'" class="w-4 h-4 inline-flex" />
        </button>
        <dropdown class="m-2 px-2" placement="bottom-end">
          <template #default>
            <div class="group flex items-center cursor-pointer select-none py-2">
              <div class="mr-1 text-gray-700 group-hover:text-blue-600 focus:text-blue-600 whitespace-nowrap">
                Export
              </div>
              <icon class="w-5 h-5 fill-gray-700 group-hover:fill-blue-600 focus:fill-blue-600" name="cheveron-down" />
            </div>
          </template>
          <template #dropdown>
            <div class="mt-2 py-2 text-sm bg-white  shadow-xl">
              <a class="block px-6 py-2 hover:text-white hover:bg-blue-500" :href="`/export${getURIParams()}`" target="_blank">PDF</a>
              <a class="block px-6 py-2 hover:text-white hover:bg-blue-500" :href="`/report/csv/${getURIParams()}`" target="_blank">CSV</a>
              <a class="block px-6 py-2 hover:text-white hover:bg-blue-500" :href="`/report/xlsx/${getURIParams()}`" target="_blank">Excel</a>
            </div>
          </template>
        </dropdown>
      </div>
    </div>
    <!-- actions -->

    <div class="mb-10">
      <bar
        id="my-chart-id"
        :options="chartOptions"
        :data="chartData"
        :style="{height: '350px'}"
      />
    </div>
    <!-- Chart -->

    <table class="table-auto w-full whitespace-nowrap">
      <thead>
        <tr class="">
          <th width="5%">
            <a class="flex items-center justify-start px-2 py-3 cursor-pointer" :class="{'text-blue-400' : form.sort == 'quote'}" role="button" @click.prevent="sortBy('quote')">
              <icon v-if="form.sort == 'quote' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
              Quote
            </a>
          </th>
          <th>
            <a class="flex items-center justify-start py-3 cursor-pointer" :class="{'text-blue-400' : form.sort == 'title'}" role="button" @click.prevent="sortBy('title')">
              <icon v-if="form.sort == 'title' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
              Project
            </a>
          </th>
          <th>
            <a class="flex items-center justify-start py-3 cursor-pointer" :class="{'text-blue-400' : form.sort == 'city'}" role="button" @click.prevent="sortBy('city')">
              <icon v-if="form.sort == 'city' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
              City
            </a>
          </th>
          <th>
            <a class="flex items-center justify-start py-3 cursor-pointer" :class="{'text-blue-400' : form.sort == 'company'}" role="button" @click.prevent="sortBy('company')">
              <icon v-if="form.sort == 'company' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
              Company
            </a>
          </th>
          <th width="10%">
            <a class="flex items-center justify-center py-3 cursor-pointer" :class="{'text-blue-400' : form.sort == 'total'}" role="button" @click.prevent="sortBy('total')">
              <icon v-if="form.sort == 'total' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
              Quote Value
            </a>
          </th>
          <th width="10%">
            <a class="flex items-center justify-center py-3 cursor-pointer" :class="{'text-blue-400' : form.sort == 'status'}" role="button" @click.prevent="sortBy('status')">
              <icon v-if="form.sort == 'status' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
              Status
            </a>
          </th>
          <th width="10%">
            <a class="flex items-center justify-center py-3 cursor-pointer" :class="{'text-blue-400' : form.sort == 'owner'}" role="button" @click.prevent="sortBy('owner')">
              <icon v-if="form.sort == 'owner' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
              Owner
            </a>
          </th>
          <th width="5%" class="">
            <a class="flex items-center justify-center py-3 cursor-pointer" :class="{'text-blue-400' : form.sort == 'created_on'}" role="button" @click.prevent="sortBy('created_on')">
              <icon v-if="form.sort == 'created_on' " :name="sortIcon" class="flex-shrink-0 ml-1 w-4 h-4 fill-blue-400" />
              Created on
            </a>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr class="bg-blue-800 text-white font-bold">
          <td class="py-3 text-right" colspan="5">${{ metrics.total }}</td>
          <td class="py-3 text-center" colspan="2">{{ metrics.rate }}% Closing Rate</td>
          <td class="py-3 pr-14 text-right">Total {{ metrics.count }} Quotes</td>
        </tr>
      </tbody>

      <tbody v-for="group in projects" :key="group.key">
        <tr class="bg-gray-100 border border-gray-200">
          <td colspan="4" class="py-3 pl-2"> {{ form.group_by == 'date' ? group.items[0].date :group.key }}</td>
          <td class="py-3 text-right">${{ group.total_str }}</td>
          <td class="py-3 text-center" colspan="2">{{ group.rate }}% Closing Rate</td>
          <td class="py-3 pr-10 text-right">
            <button type="button" @click="group.collapse = ! group.collapse">
              Total {{ group.count }} Quotes
              <icon :name="group.collapse ? 'cheveron-down' : 'cheveron-up'" class="w-4 h-4 inline-flex" />
            </button>
          </td>
        </tr>
        <tr v-for="p in group.items" v-show="! group.collapse " :key="p.id" class="border border-t-0 border-gray-100 hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="py-3 pl-2">
            <Link class="text-center focus:text-blue-500 font-bold" :href="`/projects/${p.id}`">
              {{ p.quote }}
            </Link>
          </td>
          <td class="py-3">
            <Link class=" text-left focus:text-blue-500" :href="`/projects/${p.id}`">
              <div class="whitespace-normal">{{ p.title }}</div>
            </Link>
          </td>
          <td class="py-3"><div class="whitespace-normal text-left">{{ p.city }}</div></td>
          <td class="py-3"><div class="whitespace-normal text-left">{{ p.company }}</div></td>
          <td class="py-3 text-right">${{ p.total_str }}</td>
          <td class="py-3 capitalize text-center">{{ p.status }}</td>
          <td class="py-3"><div class="whitespace-normal text-left">{{ p.owner }}</div></td>
          <td class="py-3 pr-2 text-right">{{ p.created_on }}</td>
        </tr>
        <tr><td colspan="8" class="py-4">&nbsp;</td></tr>
      </tbody>
    </table>
    <!-- Table -->
  </div>
</template>

<script>
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'

import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'
import Dropdown from '@/Shared/Dropdown'
import CheckboxInput from '@/Shared/CheckboxInput'

import DateRangePicker from 'daterange-picker-vue3'


import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

import dayjs from'dayjs'

import VMultiselect from 'vue-multiselect'


export default {
  components: {
    Head,
    Link,
    Icon,
    Dropdown,
    Bar,
    DateRangePicker,
    CheckboxInput,
    VMultiselect,
  },
  layout: Layout,
  props: {
    members  : Array,
    companies: Array,
    titles   : Array,
    owners   : Array,
    statuses : Array,
    regions  : Array,
    filters  : Object,
    projects : Object,
    metrics  : Object,
    chart    : Object,
  },
  data () {
    return {
      expandGroups: false,
      showFilters : false,
      dateRange: {
        startDate: this.filters.start,
        endDate  : this.filters.end,
      },
      form  : {
        members   : null,
        titles    : null,
        companies : null,
        statuses  : null,
        owners    : null,
        duplicated: false,
        closed    : false,
        group_by  : this.filters.groupBy,
        start     : this.filters.start,
        end       : this.filters.end,
        sort      : this.filters.sort,
        dir       : this.filters.dir,
      },
      query: {
        members   : Object.values(this.filters.members),
        titles    : Object.values(this.filters.titles),
        companies : Object.values(this.filters.companies),
        statuses  : Object.values(this.filters.statuses),
        regions   : Object.values(this.filters.regions),
        owners    : Object.values(this.filters.owners),
        duplicated: this.filters.duplicated,
        closed    : this.filters.closed,
      },
    }
  },
  computed: {
    chartData () {
      return {
        labels: this.chart.labels,
        datasets: [
          {
            label: 'Total Quotes',
            backgroundColor: '#A1A1A1',
            hoverBackgroundColor: '#002B56',
            barThickness: 10,
            maxBarThickness: 20,
            data: this.chart.count,
          },
        ],
      }
    },
    chartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 2,
        scales: {
          x: {
            grid: { display: false },
            border: {color: '#002B56'},
          },
          y:{
            grid  : { color: '#F2F2F2'},
            ticks : { color: '#002B56'},
            border: {color: '#002B56'},
          },
        },
      }
    },
    ranges () {
      let today = new Date()
      today.setHours(0, 0, 0, 0)

      let yesterday = new Date()
      yesterday.setDate(today.getDate() - 1)
      yesterday.setHours(0, 0, 0, 0)

      let last7days = new Date
      last7days.setDate(today.getDate() - 6)
      last7days.setHours(0, 0, 0, 0)

      let thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)
      thisMonthStart.setHours(0, 0, 0, 0)

      let thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      thisMonthEnd.setHours(0, 0, 0, 0)

      return {
        'Today': [today, today],
        'Yesterday': [yesterday, yesterday],
        'Last 7 days' : [today, last7days],
        'This month': [thisMonthStart, thisMonthEnd],
        'Last month': [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)],
      }

    },
    request () {
      let data = Object.assign({}, this.form)
      data.members   = data.members?.length > 0 ?  data.members.map(el =>  el.value) : null
      data.companies = data.companies?.length > 0 ?  data.companies.map(el =>  el.value) : null
      data.titles    = data.titles?.length > 0 ?  data.titles.map(el =>  el.value) : null
      data.owners    = data.owners?.length > 0 ?  data.owners.map(el =>  el.value) : null
      data.statuses  = data.statuses?.length > 0 ?  data.statuses.map(el =>  el.value) : null
      data.regions  = data.regions?.length > 0 ?  data.regions.map(el =>  el.value) : null

      return pickBy(data)
    },
    sortIcon() {
      return this.form.dir == 'DESC' ? 'cheveron-down' : 'cheveron-up'
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/', this.request , { preserveState: true })
      }, 150),
    },
  },
  methods: {
    update({startDate, endDate}) {
      this.form.start = startDate.toJSON().slice(0, 10)
      this.form.end  = endDate.toJSON().slice(0, 10)
    },
    today () {
      let d = new Date()
      d.setTime(0,0,0,0)

      this.dateRange = { startDate: d, endDate  : d }
      this.form.start = d.toJSON().slice(0, 10)
      this.form.end = d.toJSON().slice(0, 10)
    },
    toggleGroups() {
      this.expandGroups = ! this.expandGroups
      Object.values(this.projects).map(el => el.collapse = this.expandGroups)
    },
    getDate(date) {
      return dayjs(date).format('dddd, MMM DD')
    },
    getURIParams () {
      return window.location.search
    },
    filter() {
      this.form.members    = this.query.members
      this.form.titles     = this.query.titles
      this.form.companies  = this.query.companies
      this.form.owners     = this.query.owners
      this.form.statuses   = this.query.statuses
      this.form.regions    = this.query.regions
      this.form.duplicated = this.query.duplicated
      this.form.closed     = this.query.closed

      this.showFilters     = false
    },
    reset() {
      this.query.members    = null
      this.query.titles     = null
      this.query.companies  = null
      this.query.owners     = null
      this.query.statuses   = null
      this.query.regions    = null
      this.query.duplicated = false
      this.query.closed     = false
      this.filter()
    },
    sortBy(column) {
      this.form.sort = column
      this.toggleSortDir()
    },
    toggleSortDir() {
      this.form.dir = this.form.dir == 'DESC' ? 'ASC' : 'DESC'
    },
  },
}
</script>

<style src="daterange-picker-vue3/dist/daterange-picker-vue3.css"></style>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style>
/*
  Enter and leave animations can use different
  durations and timing functions.
*/
.slide-fade-enter-active {
  transition: all 0.3s ease-in;
}

.slide-fade-leave-active {
  transition: all 0.8s ease-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(100%);
}

.reportrange-text {
  border: none !important;
}

.multiselect__tags {
  border-radius: 0 !important;
  border-color: rgba(212, 212, 216, var(--tw-border-opacity)) !important;
}

.multiselect__option--highlight,
.multiselect__option--highlight::after,
.multiselect__tag {
  background-color: #0052A4 !important;
}
</style>
